import {PIXI} from '@colonist/pixi'
import {SectionBgAdaptiveView} from '../../ui_canvas_engine/SectionBgViewAdaptive'
import {UIGameManager} from '../UIGameManager'
import {UIGamePopupSelectCardContainerView} from './UIGamePopupSelectCardContainerView'
import {UIGamePopupConfirmationButtons} from './UIGamePopupConfirmationButtons'
import {UIGamePlayerAvatarView} from '../ui_game_players/UIGamePlayerAvatarView'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {SelectCardFormat} from '@colonist/game-card-types'
import {FloatingText} from '../../game_ui_view_objects/FloatingText'
import {ArrayUtils} from '@colonist/utils'
import {CardEnum, PlayerColor} from '@colonist/game-states'
import {TranslationObject} from '@colonist/i18n'

export class UIGamePopupSelectCards extends SectionBgAdaptiveView {
    protected readonly uiGameManager: UIGameManager
    protected readonly popupImageAndDescription: PIXI.Container
    protected readonly selectCardFormat: SelectCardFormat
    protected selectCardContainer: UIGamePopupSelectCardContainerView
    protected confirmationButtons: UIGamePopupConfirmationButtons
    protected victimAvatarView: UIGamePlayerAvatarView | undefined

    private onCancelButton: () => void

    // TODO: Remove me while refactoring UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
    private readonly isUserOnMobileApp: boolean

    constructor(uiGameManager: UIGameManager, popupImageAndDescription: PIXI.Container, selectCardFormat: SelectCardFormat, buildContainer: boolean = true) {
        super()
        this.uiGameManager = uiGameManager
        this.selectCardFormat = selectCardFormat

        const canvas = this.uiGameManager.canvasController
        canvas.app.stage.addChild(this)

        this.popupImageAndDescription = popupImageAndDescription
        this.addChild(this.popupImageAndDescription)

        this.isUserOnMobileApp = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
        this.createSelectCardContainer()
        this.createConfirmationButtons()
        this.addEventToCloseUIOnTapOffView()

        if(!buildContainer) return
        this.buildContainer()
    }

    private createSelectCardContainer() {
        const clickedTopResource = (cardEnum: CardEnum) => this.clickedTopResource(cardEnum)
        const clickedSelectedResource = (cardEnum: CardEnum) => this.clickedSelectedResource(cardEnum)

        const activeCards = this.selectCardFormat.validCardsToSelect
        const canvas = this.uiGameManager.canvasController
        const w = canvas.popupWidth - canvas.margin * 2
        const y = this.popupImageAndDescription.y + this.popupImageAndDescription.height
        const h = canvas.itemSize * (this.isUserOnMobileApp ? 2.1 : 1.4)
        this.selectCardContainer = new UIGamePopupSelectCardContainerView(this.uiGameManager, 0, y, w, h, clickedTopResource, clickedSelectedResource, activeCards, this, this.selectCardFormat.disabledCards?.cards)
        if(this.selectCardFormat.disableCardTypesIfBankStackIsEmpty) this.disableCardTypesIfBankStackIsEmpty()
    }

    private createConfirmationButtons() {
        const clickedCheckAction = () => this.clickedCheckAction()
        this.onCancelButton = () => this.clickedCancelAction()

        const canvas = this.uiGameManager.canvasController
        const w = canvas.popupWidth
        const buttonH = canvas.itemSize * (this.isUserOnMobileApp ? 1 : 0.55)
        const buttonY = this.absoluteHeight - buttonH
        const showCancelButton = this.selectCardFormat.cancelButtonActive && !this.isUserOnMobileApp
        this.confirmationButtons = new UIGamePopupConfirmationButtons(0, buttonY, w, buttonH, clickedCheckAction, this.onCancelButton, this, showCancelButton)
    }

    private disableCardTypesIfBankStackIsEmpty() {
        const cardTypesToDisable: CardEnum[] = []
        this.selectCardContainer.cardsToSelectFrom.enableAllCards()
        for(const cardEnum of this.selectCardFormat.validCardsToSelect) {
            const bankCardTypeCount = ArrayUtils.countOccurenceOfValueInArray(this.uiGameManager.gameController.bankState.resourceCards.cards, cardEnum)
            const selectedCardTypeCount = this.selectCardContainer.cardsSelected.cardInventory.getCardTypeCount(cardEnum)
            if(bankCardTypeCount <= selectedCardTypeCount) cardTypesToDisable.push(cardEnum)
        }
        this.selectCardContainer.cardsToSelectFrom.disableCards(cardTypesToDisable)
    }

    private addEventToCloseUIOnTapOffView() {
        if(!this.isUserOnMobileApp) return
        if(!this.selectCardFormat.cancelButtonActive) return
        this.uiGameManager.mapController.mapView.on('pointerdown', this.onCancelButton)
    }

    protected buildContainer(horizontalBuffer: number = 0, verticalBuffer: number = 0) {
        this.drawTextureBgAroundContent(horizontalBuffer, verticalBuffer)

        this.confirmationButtons.y = this.absoluteHeight - this.confirmationButtons.height

        const canvas = this.uiGameManager.canvasController
        const spaceFromBottomOfScreen = canvas.bottomViewHeight
        this.y = canvas.appH - spaceFromBottomOfScreen - this.absoluteHeight
    }

    protected clickedTopResource(cardEnum: CardEnum, animate: boolean = true) {
        if(this.didClickDisabledCard(cardEnum)) {
            this.clickedDisabledCardTypeAction(cardEnum)
            return
        }
        const clickedDisabledCard = this.selectCardContainer.cardsToSelectFrom.getDisabledCards().includes(cardEnum)
        if(clickedDisabledCard && this.selectCardFormat.disableCardTypesIfBankStackIsEmpty) return this.clickedEmptyBankCard(cardEnum)
        if(this.selectCardFormat.amountOfCardsToSelect == 1) this.resetCardSelection()
        if(this.selectedEnoughCards()) return
        this.selectCardContainer.cardsSelected.addNewCardOfType(cardEnum, animate)
        if(!this.selectCardFormat.showCardBadge) this.selectCardContainer.cardsSelected.cardInventory.hideCardBadges()
        if(this.selectCardFormat.disableCardTypesIfBankStackIsEmpty) this.disableCardTypesIfBankStackIsEmpty()
        this.updateCheckMark()
    }

    private didClickDisabledCard(cardEnum: CardEnum): boolean {
        if(this.selectCardFormat.disabledCards == undefined) return false

        return this.selectCardFormat.disabledCards.cards.includes(cardEnum)
    }

    protected clickedDisabledCardTypeAction(cardEnum: CardEnum) {
        if(this.selectCardFormat.disabledCards == undefined) return
        this.selectCardContainer.cardsToSelectFrom.shakeAllCardsOfType([cardEnum])

        const cardViews = this.selectCardContainer.cardsToSelectFrom.cardInventory.getAllCardViewsOfType(cardEnum)
        if(cardViews.length == 0) return

        const floatingText = new FloatingText(this.selectCardFormat.disabledCards.errorIfClicked, cardViews[0])
        floatingText.zIndex = 1
    }

    protected clickedEmptyBankCard(cardEnum: CardEnum) {
        this.selectCardContainer.cardsToSelectFrom.shakeAllCardsOfType([cardEnum])
        const bankMissingResourceText: TranslationObject = {key: 'strings:game.tooltips.cardCountInStack.empty'}

        const cardViews = this.selectCardContainer.cardsToSelectFrom.cardInventory.getAllCardViewsOfType(cardEnum)
        if(cardViews.length == 0) return

        const floatingText = new FloatingText(bankMissingResourceText, cardViews[0])
        floatingText.zIndex = 1
    }

    protected selectedEnoughCards() {
        const cardCount = this.selectCardContainer.cardsSelected.cardInventory.cardViews.length
        if(cardCount == this.selectCardFormat.amountOfCardsToSelect) return true
        return false
    }

    private clickedCheckAction() {
        if(!this.selectedEnoughCards()) return
        const selectedResources = this.selectCardContainer.cardsSelected.cardInventory.getAllCardEnums()
        this.uiGameManager.socketGameSend.selectCards(selectedResources)
        this.closeUI()
    }

    protected clickedSelectedResource(cardEnum: CardEnum) {
        if(this.selectCardFormat.disableCardTypesIfBankStackIsEmpty) this.disableCardTypesIfBankStackIsEmpty()
        this.updateCheckMark()
    }

    private resetCardSelection() {
        this.selectCardContainer.cardsSelected.removeAllCards()
    }

    private updateCheckMark() {
        this.confirmationButtons.checkButton.inactivateOpacity()
        if(!this.selectedEnoughCards()) return
        this.confirmationButtons.checkButton.activateOpacity()
    }

    private clickedCancelAction() {
        if(!this.selectCardFormat.cancelButtonActive) return
        this.uiGameManager.socketGameSend.cancelAction()
        this.closeUI()
    }

    closeUI() {
        if(this.parent == undefined) return
        this.parent.removeChild(this)
        this.destroy()
    }

    destroy() {
        this.uiGameManager.mapController.mapView.off('pointerdown', this.onCancelButton)
        super.destroy()
    }

    addVictimAvatarView(victim: PlayerColor) {
        const padding = this.uiGameManager.canvasController.itemSize * 0.1
        const victimAvatarSize = this.selectCardContainer.cardsToSelectFrom.height
        const playerState = this.uiGameManager.gameController.getPlayerState(victim)
        const victimAvatarX = this.selectCardContainer.width - victimAvatarSize - padding
        const victimAvatarY = this.uiGameManager.canvasController.margin
        this.victimAvatarView = new UIGamePlayerAvatarView(victimAvatarX, victimAvatarY, victimAvatarSize, this.selectCardContainer, playerState, true)
    }
}
